import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { getAttributes } from '@tiptap/react';
import { isTouch } from '../../components/utils/browser';
export var LinkMenuState = /*#__PURE__*/ function(LinkMenuState) {
    LinkMenuState[LinkMenuState["HIDDEN"] = 0] = "HIDDEN";
    LinkMenuState[LinkMenuState["VIEW_LINK_DETAILS"] = 1] = "VIEW_LINK_DETAILS";
    LinkMenuState[LinkMenuState["EDIT_LINK"] = 2] = "EDIT_LINK";
    return LinkMenuState;
}({});
/**
 * To be used in conjunction with the `LinkBubbleMenu` component, as this
 * extension provides editor commands to control the state of the link bubble
 * menu.
 *
 * The Tiptap Link extension (@tiptap/extension-link) should also be installed
 * and included in your extensions when using LinkBubbleMenuHandler:
 * https://tiptap.dev/api/marks/link.
 */ const LinkBubbleMenuHandler = Extension.create({
    name: 'linkBubbleMenuHandler',
    addStorage () {
        return {
            state: 0,
            linkNode: undefined,
            bubbleMenuOptions: undefined
        };
    },
    addCommands () {
        return {
            openLinkBubbleMenu: (bubbleMenuOptions = {})=>({ editor, chain, dispatch })=>{
                    const currentMenuState = this.storage.state;
                    let newMenuState;
                    if ((this.storage.linkNode || editor.isActive('link')) && !isTouch()) {
                        // If their cursor is currently on a link, we'll open the link menu to
                        // view the details.
                        if (!isTouch() && currentMenuState !== 1) {
                            // If the user isn't already in the "View Link Details" menu, we'll first
                            // change the selection to encompass the entire link to make it obvious which
                            // link is being edited and what text it includes. We also focus in case the
                            // user clicked the Link menu button (so we re-focus on the editor).
                            // NOTE: there is a bug in Tiptap where `extendMarkRange` will not
                            // work despite `isActive("link")` having returning true if the
                            // click/cursor is at the end of a link
                            // https://github.com/ueberdosis/tiptap/issues/2535. This leads to
                            // confusing behavior and should probably be handled with a workaround
                            // (like checking whether `extendMarkRange` had any effect) so that we
                            // don't open the link menu unless we know we've selected the entire
                            // link.
                            chain().extendMarkRange('link').focus().run();
                        }
                        newMenuState = 1;
                    } else {
                        // Otherwise open the edit link menu for the user to add a new link
                        newMenuState = 2;
                    }
                    if (dispatch) {
                        // Only change the state if this is not a dry-run
                        // https://tiptap.dev/api/commands#dry-run-for-commands. Note that
                        // this happens automatically for the Tiptap built-in commands
                        // called with `chain()` above.
                        this.storage.state = newMenuState;
                        this.storage.bubbleMenuOptions = bubbleMenuOptions;
                    }
                    return true;
                },
            editLinkInBubbleMenu: ()=>({ dispatch })=>{
                    const currentMenuState = this.storage.state;
                    const newMenuState = 2;
                    if (currentMenuState === newMenuState) {
                        return false;
                    }
                    if (dispatch) {
                        // Only change the state if this is not a dry-run
                        // https://tiptap.dev/api/commands#dry-run-for-commands.
                        this.storage.state = newMenuState;
                    }
                    return true;
                },
            closeLinkBubbleMenu: ()=>({ commands, dispatch })=>{
                    const currentMenuState = this.storage.state;
                    if (currentMenuState === 0) {
                        return false;
                    }
                    // Re-focus on the editor (e.g. for re-selection) since the user was
                    // previously editing and has now canceled
                    commands.focus();
                    if (dispatch) {
                        // Only change the state if this is not a dry-run
                        // https://tiptap.dev/api/commands#dry-run-for-commands. Note that
                        // this happens automatically for the Tiptap built-in commands
                        // called with `commands` above.
                        this.storage.state = 0;
                        this.storage.linkNode = undefined;
                    }
                    return true;
                }
        };
    },
    onSelectionUpdate () {
        if (isTouch()) {
            return; // for touch we are opening the modal
        }
        // To ensure we maintain the proper bubble menu state, if someone is
        // viewing/editing a link but moves off of it (e.g. with their keyboard
        // arrow keys, or by clicking out, or by typing over the currently selected
        // link), we'll close the bubble menu. Note that when in "view" mode (and
        // not "edit") for an existing link, we only close if the state shows the
        // user is not on an active link anymore, since the selection can be updated
        // via `openLinkBubbleMenu` (and we don't want to immediately close it upon
        // initial opening of the bubble menu). By contrast in "edit" mode, the
        // user's focus should be in the edit form and selection shouldn't
        // automatically update during opening or otherwise, so clicking out (i.e.
        // changing selection) definitively indicates cancellation.
        // onSelectionUpdate runs before handleClick, so we need to promptly close
        // in that scenario.
        if (this.storage.state === 2) {
            this.editor.commands.closeLinkBubbleMenu();
        } else if (this.storage.state === 1 && !this.editor.isActive('link')) {
            this.editor.commands.closeLinkBubbleMenu();
        }
    },
    addKeyboardShortcuts () {
        return {
            'Mod-Shift-u': ()=>{
                this.editor.commands.openLinkBubbleMenu();
                return true;
            }
        };
    },
    addProseMirrorPlugins () {
        return [
            new Plugin({
                key: new PluginKey('handleClickLinkForMenu'),
                props: {
                    handleClick: (view, pos, event)=>{
                        if (event.button !== 0) {
                            return false;
                        }
                        // Get the target HTML element and its position
                        const nodeTarget = event.target;
                        // Find the closest link element to the target element
                        const link = nodeTarget === null || nodeTarget === void 0 ? void 0 : nodeTarget.closest('a:not([data-da-type]):not([data-emote])');
                        if (!link || link.dataset.anchor) {
                            this.storage.linkNode = undefined;
                            return false;
                        }
                        const attrs = getAttributes(view.state, 'link');
                        var _attrs_href;
                        const href = (_attrs_href = attrs.href) !== null && _attrs_href !== void 0 ? _attrs_href : link.href;
                        this.storage.linkNode = link;
                        this.storage.linkNodeAttrs = {
                            href
                        };
                        this.editor.commands.openLinkBubbleMenu({
                            isNew: href === ''
                        });
                        return false;
                    }
                }
            })
        ];
    }
});
export default LinkBubbleMenuHandler;

//# sourceMappingURL=linkMenuHandler.js.map